import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { cacheMediaWaveform, getMediaWaveform, imageSrc, makeMediaErrorRetry, prefetchAudioRange } from '../api/media';
import { requestTransferAuthorization, showTransferNotice } from '../lib/transferPolicy';
import { formatSize } from '../lib/format';
import { computePeaks } from '../lib/waveform';
import { useVideoPoster } from '../lib/mediaHelpers';

/** Общие плеер голосовых/аудио и карточка документа — переиспользуются в ленте сообщений
 *  (ChatView) и в галерее вложений инфопанели (ChatInfoModal). Вынесены в отдельный файл,
 *  чтобы избежать циклического импорта между этими двумя компонентами. */

function formatTime(sec: number): string {
  const s = Math.max(0, Math.round(sec));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

const WAVE_BARS = 38;
const RATE_STEPS = [1, 1.5, 2, 3];

/** Голосовое/аудио-сообщение: своя "волна" (реальные пики амплитуды через Web Audio API,
 *  с фолбэком на обычный прогресс-бар, если декодировать не удалось), play/pause, время.
 *  showRate — кнопка скорости воспроизведения: только для голосовых, у прикреплённой
 *  музыки (kind=audio с fileName) её быть не должно — это обычный трек, не диктофонная запись. */
export function AudioPlayer({ fileId, fileName, sizeBytes, showRate = true }: { fileId?: string; fileName?: string; sizeBytes?: number; showRate?: boolean }) {
  const { t } = useTranslation('chatView');
  const [src, setSrc] = useState<string | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // Осторожная предзагрузка небольшого куска файла (см. prefetchAudioRange) — без установки
  // src и без play(), чтобы не было автовоспроизведения и полной загрузки.
  useEffect(() => {
    if (!fileId) return;
    void prefetchAudioRange(fileId, sizeBytes);
  }, [fileId, sizeBytes]);
  const [peaks, setPeaks] = useState<number[] | null>(null);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [rate, setRate] = useState(1);
  const audioRef = useRef<HTMLAudioElement>(null);
  const durationFromDecodeRef = useRef(false);
  const serverWaveformReadyRef = useRef(false);
  const durationRecoverySourcesRef = useRef(new Set<string>());
  const currentSrcRef = useRef(src);
  currentSrcRef.current = src;
  const playAfterLoadRef = useRef(false);

  const loadAudio = async () => {
    if (!fileId || loading || src) return;
    setLoading(true);
    setLoadError(null);
    const authorization = await requestTransferAuthorization({
      direction: 'download',
      label: fileName || t('media.audioFile'),
      sizeBytes: sizeBytes ?? null,
    });
    if (!authorization.allowed) {
      showTransferNotice(authorization.reason ?? t('media.wifiRequired'));
      setLoadError(authorization.reason ?? t('media.networkRequired'));
      setLoading(false);
      return;
    }
    try {
      playAfterLoadRef.current = true;
      setSrc(await imageSrc(fileId));
    } catch {
      setLoadError(t('media.audioLoadFailed'));
      playAfterLoadRef.current = false;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!src || !playAfterLoadRef.current) return;
    playAfterLoadRef.current = false;
    audioRef.current?.play().catch(() => {});
  }, [src]);

  // WebM от MediaRecorder иногда сообщает duration=Infinity. Декодируем такой файл только
  // когда браузер действительно не смог сообщить длительность, не блокируя отображение волны.
  const recoverDurationFromDecode = useCallback((source: string) => {
    if (durationRecoverySourcesRef.current.has(source)) return;
    durationRecoverySourcesRef.current.add(source);
    void (async () => {
      let ctx: AudioContext | null = null;
      try {
        const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
        const buf = await (await fetch(source)).arrayBuffer();
        ctx = new AudioCtx();
        const decoded = await ctx.decodeAudioData(buf);
        if (currentSrcRef.current === source && Number.isFinite(decoded.duration) && decoded.duration > 0) {
          durationFromDecodeRef.current = true;
          setDuration(decoded.duration);
        }
      } catch {
        // Длительность остаётся на значении HTMLAudioElement, если WebAudio не поддерживает файл.
      } finally {
        ctx?.close().catch(() => {});
      }
    })();
  }, []);

  // Берём пики из постоянного кэша/API. WebAudio остаётся последовательным запасным путём.
  useEffect(() => {
    if (!fileId && !src) return;
    let cancelled = false;
    (async () => {
      try {
        serverWaveformReadyRef.current = false;
        durationFromDecodeRef.current = false;
        let bars: number[];
        try {
          if (!fileId) throw new Error('Нет id медиа');
          bars = (await getMediaWaveform(fileId)).peaks;
          if (!cancelled) {
            serverWaveformReadyRef.current = true;
            setPeaks(bars);
            const audio = audioRef.current;
            if (src && audio && audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
              if (Number.isFinite(audio.duration) && audio.duration > 0) setDuration(audio.duration);
              else recoverDurationFromDecode(src);
            }
          }
        } catch {
          if (!src) return;
          // 404/503/сеть/таймаут не видны пользователю: локально считаем как раньше.
          let ctx: AudioContext | null = null;
          try {
            const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
            const buf = await (await fetch(src)).arrayBuffer();
            ctx = new AudioCtx();
            const decoded = await ctx.decodeAudioData(buf);
            bars = computePeaks(decoded.getChannelData(0), WAVE_BARS);
            if (!cancelled && Number.isFinite(decoded.duration) && decoded.duration > 0) {
              durationFromDecodeRef.current = true;
              setDuration(decoded.duration);
            }
            if (!cancelled) {
              setPeaks(bars);
              if (fileId) cacheMediaWaveform(fileId, bars);
            }
          } finally {
            // Закрытие обязательно даже при ошибке декодирования: браузеры ограничивают число
            // одновременно открытых AudioContext.
            ctx?.close().catch(() => {});
          }
        }
      } catch {
        if (!cancelled) setPeaks(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [src, fileId, recoverDurationFromDecode]);

  // Chrome/webm-баг: у WebM-блобов от MediaRecorder в заголовке нет длительности, поэтому
  // audio.duration репортится как Infinity, пока не проиграть/промотать до конца хотя бы раз.
  // Раньше это лечилось форс-сиком в "далёкое будущее", но если сервер не отдаёт медиа с
  // поддержкой Range-запросов, такой сик молча зависает и калечит все последующие
  // перемотки (элемент застревал в состоянии "seeking"). Настоящая длительность уже надёжно
  // приходит из декодированного буфера (см. эффект выше) — этот хэндлер лишь подхватывает
  // её, если audio.duration и так конечен, без рискованных трюков.
  const fixInfiniteDuration = (a: HTMLAudioElement) => {
    if (Number.isFinite(a.duration) && !durationFromDecodeRef.current) {
      setDuration(a.duration || 0);
    }
  };

  if (!src) return (
    <div className="audio-msg-wrap">
      {fileName && <div className="audio-msg-name" title={fileName}>{fileName}</div>}
      <div className="audio-msg">
        <button type="button" className="audio-msg-play" onClick={loadAudio} disabled={loading} title={t('media.loadAndPlay')}>
          <img src="/play.svg" alt={t('media.loadAndPlay')} />
        </button>
        <span className="audio-msg-time" style={{ color: loadError ? 'var(--error)' : undefined }}>
          {loading ? t('media.preparing') : loadError || t('media.loadAudio')}
        </span>
      </div>
    </div>
  );

  const progress = duration ? current / duration : 0;
  const seek = (ratio: number) => {
    const a = audioRef.current;
    if (!a || !duration || !Number.isFinite(duration)) return;
    const t = Math.min(1, Math.max(0, ratio)) * duration;
    a.currentTime = t;
    // Обновляем позицию сразу, не дожидаясь события timeupdate — перемотка должна
    // двигать волну/точку сразу, даже пока сообщение стоит на паузе, и никак не
    // должна сама по себе запускать воспроизведение (play() тут не вызывается).
    setCurrent(t);
  };
  const cycleRate = () => {
    const next = RATE_STEPS[(RATE_STEPS.indexOf(rate) + 1) % RATE_STEPS.length];
    setRate(next);
    if (audioRef.current) audioRef.current.playbackRate = next;
  };

  return (
    <div className="audio-msg-wrap">
    {/* Имя файла как заголовок — только у прикреплённой музыки (fileName есть), у голосовых
        (запись с кнопки) его нет, заголовок не показываем (см. отчёт пользователя). */}
    {fileName && <div className="audio-msg-name" title={fileName}>{fileName}</div>}
    <div className="audio-msg">
      <button
        type="button"
        className="audio-msg-play"
        onClick={() => (playing ? audioRef.current?.pause() : audioRef.current?.play())}
      >
        <img src={playing ? '/pause.svg' : '/play.svg'} alt={playing ? t('media.pause') : t('media.play')} />
      </button>
      {peaks ? (
        <div
          className="audio-msg-wave"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seek((e.clientX - rect.left) / rect.width);
          }}
        >
          {peaks.map((p, i) => (
            <span
              key={i}
              className={'audio-bar' + (i / WAVE_BARS < progress ? ' played' : '')}
              style={{ height: `${Math.round(p * 100)}%` }}
            />
          ))}
        </div>
      ) : (
        <div
          className="audio-msg-track"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seek((e.clientX - rect.left) / rect.width);
          }}
        >
          <div className="audio-msg-track-fill" style={{ width: `${progress * 100}%` }} />
        </div>
      )}
      <div className="audio-msg-time">{formatTime(playing || current ? current : duration)}</div>
      {showRate && (
        <button type="button" className="audio-msg-rate" title={t('media.playbackRate')} onClick={cycleRate}>
          {rate}×
        </button>
      )}
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        style={{ display: 'none' }}
        onError={makeMediaErrorRetry(fileId, src, setSrc)}
        onLoadedMetadata={(e) => {
          e.currentTarget.playbackRate = rate;
          fixInfiniteDuration(e.currentTarget);
          if (!Number.isFinite(e.currentTarget.duration) && serverWaveformReadyRef.current) recoverDurationFromDecode(src);
        }}
        onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrent(0);
        }}
      />
    </div>
    </div>
  );
}

export function DocumentCard({ fileId, fileName, sizeBytes }: { fileId?: string; fileName?: string; sizeBytes?: number }) {
  const { t } = useTranslation('chatView');
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const menuButtonRef = useRef<HTMLButtonElement>(null);
  const menuRect = menuButtonRef.current?.getBoundingClientRect();
  const transfer = async (save: boolean) => {
    if (!fileId || loading) return;
    setMenuOpen(false);
    setLoading(true);
    setLoadError(null);
    // Окно и системный диалог открываем в самом жесте пользователя, до сетевых await.
    let tab: Window | null = null;
    type SaveHandle = { createWritable: () => Promise<{ write: (blob: Blob) => Promise<void>; close: () => Promise<void> }> };
    const picker = (window as Window & { showSaveFilePicker?: (options: { suggestedName: string }) => Promise<SaveHandle> }).showSaveFilePicker;
    try {
      tab = save ? null : window.open('about:blank', '_blank');
      if (tab) tab.opener = null;
      const destination = save && picker ? picker.call(window, { suggestedName: fileName || 'file' }) : null;
      const handle = destination ? await destination : null;
      const authorization = await requestTransferAuthorization({
        direction: 'download', label: fileName || t('media.file'), sizeBytes: sizeBytes ?? null,
      });
      if (!authorization.allowed) {
        tab?.close();
        const reason = authorization.reason ?? t('media.wifiRequired');
        setLoadError(reason);
        showTransferNotice(reason);
        return;
      }
      const url = await imageSrc(fileId);
      if (!save) {
        if (!tab) throw new Error('Окно заблокировано браузером');
        tab.location.replace(url);
        return;
      }
      const response = await fetch(url);
      if (!response.ok) throw new Error('Не удалось получить файл');
      const blob = await response.blob();
      if (handle) {
        const writable = await handle.createWritable();
        await writable.write(blob);
        await writable.close();
      } else {
        // В браузерах без системного диалога место сохранения выбирают настройки загрузок.
        const objectUrl = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = objectUrl;
        anchor.download = fileName || 'file';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        window.setTimeout(() => URL.revokeObjectURL(objectUrl), 60_000);
      }
    } catch (error) {
      tab?.close();
      if (!(error instanceof Error && error.name === 'AbortError')) setLoadError(t('media.downloadFailed'));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="doc-card-wrap">
      <button type="button" className="doc-card" title={t('media.open')} onClick={() => transfer(false)} disabled={loading || !fileId} style={{ width: '100%', textAlign: 'left', border: 0, cursor: loading ? 'wait' : 'pointer' }}>
        <div className="doc-icon-wrap">
          <img src="/document.svg" alt="" className="doc-icon" />
        </div>
        <div className="doc-info">
          <div className="doc-name">{fileName || t('media.file')}</div>
          <div className="doc-size">{loading ? t('media.preparing') : loadError || formatSize(sizeBytes) || t('media.open')}</div>
        </div>
      </button>
      <button type="button" className="row-menu-btn" ref={menuButtonRef} title={t('media.more')} onClick={() => setMenuOpen(!menuOpen)} disabled={loading || !fileId}>⋮</button>
      {menuOpen && createPortal(<>
        <div className="menu-backdrop" onClick={() => setMenuOpen(false)} />
        <div className="menu" style={{ position: 'fixed', top: Math.max(8, Math.min(menuRect?.bottom ?? 8, window.innerHeight - 60)), left: Math.max(8, Math.min(menuRect?.left ?? 8, window.innerWidth - 210)), right: 'auto' }}><button type="button" className="menu-item" onClick={() => transfer(true)}>{t('media.saveAs')}</button></div>
      </>, document.body)}
    </div>
  );
}

/** Круглый видеоплеер видео-сообщения (запись без fileName). Вынесен сюда, чтобы его
 *  переиспользовали лента (ChatView) и категория «Голосовые» инфопанели (ChatInfoModal) — без
 *  циклического импорта. Кружок play виден всегда, пока видео не играет (см. отчёт
 *  пользователя "воспроизведение из медиа-панели, чтобы был кружок всегда"). */
export function RoundVideoPlayer({ fileId }: { fileId?: string }) {
  const { t } = useTranslation('chatView');
  const { poster, loaded } = useVideoPoster(fileId);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const toggle = async () => {
    if (!fileId) return;
    if (!src) {
      if (loading) return;
      setLoading(true);
      try {
        setSrc(await imageSrc(fileId));
      } catch {
        /* не критично — просто не запустится в этот раз */
      } finally {
        setLoading(false);
      }
      return; // play() — в эффекте ниже, сразу после монтирования <video> с этим src
    }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  // Между тапом и монтированием <video> есть await imageSrc() — цепочка "жеста пользователя"
  // для браузерной autoplay-политики к этому моменту уже разорвана, один атрибут autoPlay
  // в таких случаях молча не срабатывает. Явный play() тут же после монтирования срабатывает
  // надёжнее; если браузер всё равно откажет — кружок просто останется на паузе с той же
  // иконкой play поверх (playing тогда так и остаётся false), повторный тап сработает точно.
  useEffect(() => {
    if (src) videoRef.current?.play().catch(() => {});
  }, [src]);

  if (!loaded && !src) return <div style={{ color: 'var(--muted)' }}>{t('media.loading')}</div>;
  return (
    <div className="video-round" onClick={toggle}>
      {src ? (
        <video
          ref={videoRef}
          src={src}
          poster={poster ?? undefined}
          playsInline
          preload="metadata"
          onError={makeMediaErrorRetry(fileId, src, setSrc)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => setPlaying(false)}
        />
      ) : poster ? (
        <img src={poster} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
      ) : null}
      {!playing && (
        <div className="video-round-play">
          <img src="/play.svg" alt="" />
        </div>
      )}
    </div>
  );
}

/** Видео-кружок в категории «Голос» инфопанели — та же строка, что у голосовых сообщений
 *  (play-кнопка + прогресс + время), но вместо стрелки в круглой кнопке — кадр видео (см.
 *  отчёт пользователя "видео кружки отображать так же как аудио сообщения только с
 *  миниатюрой вместо иконки"). Сам видеоэлемент скрыт — играет только звук, как у голосовых,
 *  строка не превращается в отдельный видеоплеер. */
export function VideoVoiceRow({ fileId }: { fileId?: string }) {
  const { t } = useTranslation('chatView');
  const { poster } = useVideoPoster(fileId);
  const [src, setSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [duration, setDuration] = useState(0);
  const [current, setCurrent] = useState(0);
  const [playing, setPlaying] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (src) videoRef.current?.play().catch(() => {});
  }, [src]);

  const toggle = async () => {
    if (!fileId) return;
    if (!src) {
      if (loading) return;
      setLoading(true);
      try {
        setSrc(await imageSrc(fileId));
      } catch {
        /* не критично — просто не запустится в этот раз */
      } finally {
        setLoading(false);
      }
      return;
    }
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const progress = duration ? current / duration : 0;
  const seek = (ratio: number) => {
    const v = videoRef.current;
    if (!v || !duration || !Number.isFinite(duration)) return;
    const t = Math.min(1, Math.max(0, ratio)) * duration;
    v.currentTime = t;
    setCurrent(t);
  };

  return (
    <div className="audio-msg-wrap">
      <div className="audio-msg">
        <button type="button" className="audio-msg-play video-thumb-play" onClick={toggle}>
          {poster && <img src={poster} alt="" className="audio-msg-play-thumb" />}
          <img src={playing ? '/pause.svg' : '/play.svg'} alt={playing ? t('media.pause') : t('media.play')} className="audio-msg-play-glyph" />
        </button>
        <div
          className="audio-msg-track"
          onClick={(e) => {
            const rect = e.currentTarget.getBoundingClientRect();
            seek((e.clientX - rect.left) / rect.width);
          }}
        >
          <div className="audio-msg-track-fill" style={{ width: `${progress * 100}%` }} />
        </div>
        <div className="audio-msg-time">{formatTime(playing || current ? current : duration)}</div>
        <video
          ref={videoRef}
          src={src ?? undefined}
          preload="metadata"
          style={{ display: 'none' }}
          onError={makeMediaErrorRetry(fileId, src, setSrc)}
          onLoadedMetadata={(e) => {
            // Тот же баг Chrome/webm, что и у AudioPlayer.fixInfiniteDuration: duration
            // MediaRecorder-записи репортится как Infinity, пока не сыграть/промотать до
            // конца — иначе formatTime рисует "Infinity:NaN", а seek() отказывается работать.
            const d = e.currentTarget.duration;
            if (Number.isFinite(d)) setDuration(d || 0);
          }}
          onTimeUpdate={(e) => setCurrent(e.currentTarget.currentTime)}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onEnded={() => {
            setPlaying(false);
            setCurrent(0);
          }}
        />
      </div>
    </div>
  );
}
